import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { NavButton } from "@/components/ui/navigation";
import { TextSkeleton } from "@/features/projects/components/text-skeleton";

const THUMBNAIL_COUNT = 3;
const SCENE_COUNT = 5;
const QUALITY_SCORES = ["Hook", "Visual ease", "Hold"];

/*
 * VideoInDepthSkeleton component
 * Skeleton component used while a video's in-depth report is loading, mirroring
 * ReportsVideoInDepthRoute's layout: thumbnail rail, player/graph/scenes column, and metrics panel.
 */
export const VideoInDepthSkeleton = () => {
  /* Render */
  return (
    <>
      {/* Header */}
      <div className="flex flex-row items-center justify-end gap-2">
        <div className="flex flex-row items-center mr-auto">
          <NavButton />
          <TextSkeleton variant="h2" className="ml-2 w-40" />
        </div>
      </div>

      <div className="grow flex flex-row gap-4 min-w-0">
        {/* Thumbnails */}
        <div className="flex flex-col gap-4">
          {Array.from({ length: THUMBNAIL_COUNT }).map((_, index) => (
            <div key={index} className="skeleton shrink-0 size-[54px] rounded-lg" />
          ))}
        </div>

        {/* Video Player + Attention Graph + Scenes */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {/* Player */}
          <div className="skeleton relative w-full aspect-[895/441] rounded-lg min-h-[450px]" />

          {/* Attention graph */}
          <div className="flex flex-col gap-4 bg-[#242c35] rounded-lg p-4 min-w-0">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold">Attention</h2>
              <div className="flex items-center gap-1.5 opacity-70">
                <span className="text-white text-xs font-semibold">About this graph</span>
                <InformationCircleIcon className="size-3.5 text-white" />
              </div>
            </div>

            <div className="flex gap-2">
              {QUALITY_SCORES.concat("Attention").map((metric) => (
                <div key={metric} className="skeleton h-[31px] w-20 rounded-lg" />
              ))}
            </div>

            <div className="skeleton w-full" style={{ height: 149 }} />

            <div className="flex gap-0.5 w-full">
              {Array.from({ length: SCENE_COUNT }).map((_, index) => (
                <div key={index} className="skeleton h-5 flex-1 rounded" />
              ))}
            </div>
          </div>

          {/* Scenes strip */}
          <div className="flex flex-col gap-3 bg-[#242c35] rounded-lg p-4 min-w-0">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold">Scenes</h2>
              <div className="flex items-center gap-1.5 opacity-70">
                <span className="text-white text-xs font-semibold">About scenes</span>
                <InformationCircleIcon className="size-3.5 text-white" />
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto min-w-0">
              {Array.from({ length: SCENE_COUNT }).map((_, index) => (
                <div key={index} className="flex flex-col items-start gap-1.5 w-[89px] shrink-0 rounded-lg border-2 border-[#323c49] px-1 py-2">
                  <div className="skeleton w-full h-11 rounded" />
                  <div className="skeleton h-3 w-12 rounded" />
                  <div className="skeleton h-3 w-8 rounded" />
                </div>
              ))}
            </div>
          </div>

          {/* Scene detail */}
          <div className="skeleton h-[200px] w-full rounded-2xl" />
        </div>

        {/* Metrics panel */}
        <div className="flex flex-col gap-4 w-[443px] shrink-0">
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  <h2 className="font-semibold">Attention</h2>
                  <InformationCircleIcon className="size-4 text-base-content/50" />
                </div>
                <p className="text-sm text-base-content/70 max-w-[236px]">The overall Attention score of the video.</p>
              </div>
              <TextSkeleton variant="h2" className="w-14" />
            </div>
            <div className="skeleton w-full h-2 rounded-lg" />
            <div className="skeleton w-full h-16 rounded-lg" />
          </div>

          <h2 className="font-semibold">Quality scores</h2>
          <div className="flex flex-col gap-px rounded-lg overflow-hidden">
            {QUALITY_SCORES.map((label) => (
              <div key={label} className="flex items-center justify-between bg-base-200 px-4 py-3">
                <span className="text-sm font-semibold">{label}</span>
                <div className="skeleton h-4 w-10 rounded-lg" />
              </div>
            ))}
          </div>

          <div className="skeleton w-full h-[214px] rounded-lg" />
        </div>
      </div>
    </>
  );
};
